import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/App";
import { LEVEL_STYLES, LevelGem } from "@/components/CreatorLevelBadge";

let lastCheckAt = 0;
const POLL_MS = 45000;
const MIN_GAP_MS = 20000;
const CONFETTI_COLORS = ["#f97316", "#ec4899", "#a855f7", "#60a5fa", "#fbbf24", "#34d399", "#f43f5e"];

const Confetti = () => (
  <div className="levelup-confetti" aria-hidden="true">
    {Array.from({ length: 42 }).map((_, i) => {
      const left = (i * 37) % 100;
      const delay = ((i * 13) % 20) / 10;
      const dur = 2.6 + ((i * 7) % 14) / 10;
      const size = 6 + ((i * 5) % 8);
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      return (
        <span
          key={i}
          style={{
            left: `${left}%`,
            width: size,
            height: size * (i % 3 === 0 ? 2.2 : 1),
            background: color,
            borderRadius: i % 2 === 0 ? "50%" : "2px",
            animationDelay: `${delay}s`,
            animationDuration: `${dur}s`,
          }}
        />
      );
    })}
  </div>
);

export const LevelUpCelebration = () => {
  const [celebration, setCelebration] = useState(null);

  const check = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckAt < MIN_GAP_MS) return;
    lastCheckAt = now;
    try {
      const res = await api.get("/notifications");
      const notif = (res.data.notifications || []).find(
        (n) => !n.read && (n.type === "creator_level_up" || n.data?.type === "creator_level_up")
      );
      if (notif) {
        api.post(`/notifications/${notif.notification_id || notif.id}/read`).catch(() => {});
        setCelebration({
          level: notif.data?.level || 1,
          levelName: notif.data?.level_name || "",
        });
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    check();
    const iv = setInterval(() => { lastCheckAt = 0; check(); }, POLL_MS);
    return () => clearInterval(iv);
  }, [check]);

  if (!celebration) return null;

  const s = LEVEL_STYLES[celebration.level] || LEVEL_STYLES[1];

  return createPortal(
    <div className="levelup-overlay" data-testid="levelup-celebration" onClick={() => setCelebration(null)}>
      <style>{`
        .levelup-overlay {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          background: rgba(4, 4, 10, 0.88);
          backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
          animation: levelup-fade 0.4s ease-out;
        }
        .levelup-glow {
          position: absolute; width: 480px; height: 480px; border-radius: 50%;
          background: radial-gradient(circle, ${s.ring}33 0%, transparent 65%);
          animation: levelup-pulse 2.4s ease-in-out infinite;
        }
        .levelup-card { position: relative; text-align: center; padding: 0 32px; animation: levelup-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .levelup-ring {
          width: 120px; height: 120px; margin: 0 auto 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 2px solid ${s.ring}66;
          background: linear-gradient(160deg, ${s.ring}1f, transparent 70%);
          box-shadow: 0 0 60px ${s.ring}59, inset 0 1px 0 rgba(255,255,255,0.15);
        }
        .levelup-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
        .levelup-confetti span {
          position: absolute; top: -6vh; display: block; opacity: 0;
          animation-name: levelup-fall; animation-timing-function: linear; animation-iteration-count: infinite;
        }
        @keyframes levelup-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(115vh) rotate(660deg); opacity: 0.7; }
        }
        @keyframes levelup-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes levelup-pop { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes levelup-pulse { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.12); opacity: 1; } }
      `}</style>
      <div className="levelup-glow" />
      <Confetti />
      <div className="levelup-card" onClick={(e) => e.stopPropagation()}>
        <div className="levelup-ring">
          <LevelGem level={celebration.level} size={86} spin />
        </div>
        <p className="text-[11px] font-bold tracking-[0.4em] uppercase text-white/50 mb-2">Level Up</p>
        <h2
          className="text-5xl font-black tracking-tight text-white mb-3"
          style={{ textShadow: `0 0 40px ${s.ring}80` }}
          data-testid="levelup-level-name"
        >
          {celebration.levelName}
        </h2>
        <p className="text-sm text-white/60 mb-8">Hai raggiunto un nuovo livello Creator.<br />Continua così!</p>
        <button
          onClick={() => setCelebration(null)}
          className="px-8 py-3 rounded-full text-sm font-semibold text-white active:scale-95 transition-transform"
          style={{ background: `linear-gradient(120deg, ${s.ring}cc, ${s.ring}66)`, boxShadow: `0 8px 32px ${s.ring}4d` }}
          data-testid="levelup-close-btn"
        >
          Continua
        </button>
      </div>
    </div>,
    document.body
  );
};

export default LevelUpCelebration;
